import {
  CloseOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Checkbox, Progress, Tooltip } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlayCircle } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import { useGlobalDownloader } from "../../context/global/DownloadProvider";
import { useInfoUser } from "../../store/UserStore";
import { formatTime, formatTimeBySeconds } from "../../utils/formatTime";
import { getFileIcon } from "../../utils/SendFile";
import {
  isAudioFile,
  isImageFile,
  isSupportedFileType,
  isVideoFile,
  openNotificationError,
  standardizeVolume,
} from "../../utils/Utils";
import FilePreviewModal from "../modal/FilePreviewModal";
import MediaStorageModal from "../modal/MediaStorageModal";
import videoPreviewDefault from "../../assets/video_preview_default.jpg";
import noImage from "../../assets/no-image.avif";

const fileStore = process.env.REACT_APP_FILE_STORE || "http://10.1.1.230:8000";
const getStorageDownloadPath = (file) => {
  if (!file?.path) return file?.resourceId;

  if (/^https?:\/\//i.test(file.path)) return file.path;

  return `${fileStore}/conversation/${file?.conversationId}${file.path}`;
};

const PreviewFile = ({
  file,
  handleOpenMediaModal,
  openFilePreviewModal,
  previewFileError,
  setPreviewFileError,
}) => {
  const [thumbError, setThumbError] = useState(false);

  const fileName = file?.originalName || file?.fileName || "";
  const extension = fileName?.includes(".")
    ? fileName.substring(fileName.lastIndexOf(".") + 1).toLowerCase()
    : "";

  const imageExtensions = [
    "jpg", "jpeg", "png", "gif", "bmp", "webp", "jfif", "svg", "ico", "tif", "tiff", "heic", "heif", "avif"
  ];
  const videoExtensions = [
    "mp4", "avi", "mkv", "webm", "mov", "quicktime", "flv", "wmv", "3gp", "m4v"
  ];

  const isImageByExt = extension && imageExtensions.includes(extension);
  const isVideoByExt = extension && videoExtensions.includes(extension);

  const isVideo =
    isVideoByExt ||
    Boolean(file?.videoImgResourceId || file?.videoImgPreviewPath) ||
    (!extension && (
      file?.contentType === "VIDEO" ||
      file?.type === "VIDEO" ||
      file?.actualContentType?.startsWith("video/")
    ));

  const isImage =
    isImageByExt ||
    (!extension && (
      file?.contentType === "IMAGE" ||
      file?.type === "IMAGE" ||
      file?.actualContentType?.startsWith("image/")
    ));

  const handleMediaClick = (e) => {
    e.stopPropagation();
    if (isImage || isVideo) {
      handleOpenMediaModal();
    } else if (
      openFilePreviewModal &&
      isSupportedFileType(fileName)
    ) {
      openFilePreviewModal();
    }
  };

  if (isVideo) {
    const videoThumb =
      file?.videoImgPreviewPath ||
      file?.thumbnailPreviewPath ||
      videoPreviewDefault;
    return (
      <div
        className="bg-gray-900 w-full h-[145px] rounded cursor-pointer flex items-center justify-center select-none overflow-hidden relative group"
        onClick={handleMediaClick}
      >
        <img
          src={videoThumb}
          alt={fileName || "video preview"}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = videoPreviewDefault;
          }}
        />
        <div className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition flex items-center justify-center pointer-events-none">
          <div className="p-2 bg-black/50 backdrop-blur-sm rounded-full text-white shadow-lg group-hover:scale-110 transition-transform">
            <FaPlayCircle size={26} />
          </div>
        </div>
      </div>
    );
  }

  if (isImage) {
    const imgSrc = file?.thumbnailPreviewPath || file?.path || noImage;
    return (
      <div
        className="bg-gray-100 w-full h-[145px] rounded cursor-pointer flex items-center justify-center select-none overflow-hidden relative group"
        onClick={handleMediaClick}
      >
        <img
          src={imgSrc}
          alt={fileName || "image preview"}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
      </div>
    );
  }

  if (file?.googleDriveFileId && !previewFileError) {
    const urlThumbnail = `https://images.weserv.nl/?url=drive.google.com/thumbnail?id=${file.googleDriveFileId}&width=300`;
    return (
      <div
        className="bg-gray-100 w-full h-[145px] rounded cursor-pointer flex items-center justify-center select-none overflow-hidden relative group"
        onClick={handleMediaClick}
      >
        <img
          src={urlThumbnail}
          alt={fileName || "drive file"}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={() => setPreviewFileError(true)}
        />
      </div>
    );
  }

  if (file?.thumbnailPreviewPath && !thumbError) {
    return (
      <div
        className="bg-gray-100 w-full h-[145px] rounded cursor-pointer flex items-center justify-center select-none overflow-hidden relative group"
        onClick={handleMediaClick}
      >
        <img
          src={file.thumbnailPreviewPath}
          alt={fileName || "preview"}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={() => {
            setThumbError(true);
          }}
        />
      </div>
    );
  }


  return (
    <div
      className="bg-[#f8fafc] w-full h-[145px] rounded flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-slate-100 transition select-none"
      onClick={handleMediaClick}
    >
      <div className="scale-110 transform text-gray-500">
        {getFileIcon(fileName)}
      </div>
      <span className="text-[9px] font-medium text-gray-500 uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-200/70">
        {extension || "FILE"}
      </span>
    </div>
  );
};

const StorageFile = ({ file, pickFile, pickedList }) => {
  const { languageMap } = useInfoUser();

  const [urlPreview, setUrlPreview] = useState(null);
  const [isOpenMediaModal, setIsOpenMediaModal] = useState(false);

  const [isFilePreviewModal, setIsFilePreviewModal] = useState(false);
  const [previewFileError, setPreviewFileError] = useState(false);

  const [fileChoose, setFileChoose] = useState(null);

  const downloadPath = getStorageDownloadPath(file);
  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  const [fileState, setFileState] = useState({});
  const fileNameToSave = file?.originalName || file?.fileName || "Willo-video";

  const handleResumeDownload = (e) => {
    e.stopPropagation();
    resumeDownload(downloadPath, fileNameToSave);
  };

  const handlePauseDownload = (e) => {
    e.stopPropagation();
    pauseDownload(downloadPath);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    startDownload(downloadPath, fileNameToSave, 0);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelDownload(downloadPath);
  };

  const openFilePreviewModal = () => {
    if (!isSupportedFileType(file?.originalName || file?.fileName)) return;

    const pathUrl = file?.path;

    if (!pathUrl) {
      openNotificationError(
        languageMap?.["preview.error.pathNotFound"] ??
        "Error opening file due to non-existent path",
      );
      return;
    }

    if (pathUrl.startsWith("http://") || pathUrl.startsWith("https://")) {
      setUrlPreview(pathUrl);
    } else {
      setUrlPreview(
        `${fileStore}/conversation/${file?.conversationId}${pathUrl}`,
      );
    }

    setIsFilePreviewModal(true);
  };

  const renderDownloadButton = useCallback(() => {
    switch (fileState?.status) {
      case DOWNLOAD_STATUS.PAUSED:
        return (
          <div className="flex gap-1 items-center">
            <button onClick={handleResumeDownload} className="flex items-center">
              <PlayCircleOutlined
                style={{ fontSize: "14px", color: "green" }}
              />
            </button>
            <button onClick={handleCancel} className="flex items-center">
              <CloseOutlined style={{ fontSize: "13px", color: "red" }} />
            </button>
          </div>
        );
      case DOWNLOAD_STATUS.DOWNLOADING:
        return (
          <div className="flex gap-1 items-center">
            <button onClick={handlePauseDownload} className="flex items-center">
              <PauseOutlined className="text-[14px] text-red" />
            </button>
            <button onClick={handleCancel} className="flex items-center">
              <CloseOutlined className="text-[13px] text-red" />
            </button>
          </div>
        );

      case DOWNLOAD_STATUS.SUCCESS:
        return (
          <button onClick={handleDownload} className="flex items-center">
            <IoMdDownload className="text-[14px] text-[#2b9853]" />
          </button>
        );
      case DOWNLOAD_STATUS.ERROR:
        return (
          <div className="flex gap-1 items-center">
            <button onClick={handleCancel} className="flex items-center">
              <CloseOutlined style={{ fontSize: "13px", color: "red" }} />
            </button>
          </div>
        );
      default:
        return (
          <button onClick={handleDownload} className="flex items-center">
            <IoMdDownload className="text-[14px] text-[#2b9853]" />
          </button>
        );
    }
  }, [fileState?.status, downloadPath, fileNameToSave]);

  const handleOpenMediaModal = () => {
    const fileName = file?.originalName || file?.fileName;
    const isImage = isImageFile(fileName) || false;
    const isAudio = isAudioFile(fileName) || false;
    const isVideo = isVideoFile(fileName) || false;

    let fileData = { ...file };

    if (isVideo) {
      fileData.videoImgResourceId = file?.resourceId;
    }
    fileData.mainPath = fileData?.mainPath || fileData?.path;
    setFileChoose(fileData);

    if (isAudio) {
      return;
    }

    if (isImage || isVideo) {
      setIsOpenMediaModal(true);
    } else {
      openFilePreviewModal();
    }
  };

  const chooseFile = () => {
    pickFile(file?.resourceAccessId);
  };

  const showInfoFile = useMemo(() => {
    if (
      file?.actualContentType?.startsWith("video/") &&
      (file?.videoImgResourceId || file?.videoImgPreviewPath)
    ) {
      return <span>{formatTimeBySeconds(Math.round(file?.duration))}</span>;
    }

    return <span>{standardizeVolume(file?.volume)}</span>;
  }, [file]);

  const isPicked = useMemo(() => {
    return pickedList?.includes(file?.resourceAccessId);
  }, [pickedList, file?.resourceAccessId]);

  const progressRate = useMemo(() => {
    return (
      <div className="flex gap-[2px]">
        <div>
          {fileState?.progress
            ? standardizeVolume((fileState?.progress * file?.volume) / 100)
            : 0}
        </div>
        <div>/</div>
        <div>{standardizeVolume(file?.volume)}</div>
      </div>
    );
  }, [file?.volume, fileState?.progress]);

  useEffect(() => {
    setFileState(downloads[downloadPath]);
  }, [downloads, downloadPath]);

  return (
    <>
      <div className="flex flex-col w-full max-w-[270px] min-w-0">
        <div
          onClick={chooseFile}
          className={`w-full rounded p-2 transition-all duration-200 cursor-pointer border flex flex-col justify-between gap-1 select-none ${isPicked
            ? "bg-[#e2edfd] border-[#3b82f6] shadow-sm ring-1 ring-[#3b82f6]/40"
            : "bg-[#f1f4f8] hover:bg-[#e5ebf2] border-[#d8e0ea] hover:border-[#cbd5e1] hover:shadow-sm"
            }`}
        >
          {/* ===== 1. Header: Checkbox + Title + Download button ===== */}
          <div className="flex items-center justify-between gap-1 min-w-0 py-0">
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <Checkbox
                onChange={() => chooseFile()}
                onClick={(e) => e.stopPropagation()}
                checked={isPicked}
                className="flex-shrink-0 scale-[0.75] origin-center"
              />
              <Tooltip title={file?.originalName || file?.fileName} placement="topLeft">
                <span className="font-medium text-[10px] truncate flex-1 block leading-tight text-gray-800">
                  {file?.originalName || file?.fileName}
                </span>
              </Tooltip>
            </div>

            <div
              className="flex-shrink-0 p-0.5 rounded text-gray-600 hover:text-black hover:bg-gray-200/50 transition flex items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {renderDownloadButton()}
            </div>
          </div>

          {/* ===== 2. Preview File (Thumbnail / Icon) ===== */}
          <PreviewFile
            file={file}
            handleOpenMediaModal={handleOpenMediaModal}
            openFilePreviewModal={openFilePreviewModal}
            previewFileError={previewFileError}
            setPreviewFileError={setPreviewFileError}
          />

          {/* ===== 3. Footer: Date & File size ===== */}
          <div className="flex items-center justify-between text-[9.5px] text-gray-500 min-w-0 py-0 leading-tight">
            <span className="truncate">{formatTime(file?.createdAt)}</span>
            <span className="font-medium ml-1.5 whitespace-nowrap flex-shrink-0">
              {showInfoFile}
            </span>
          </div>
        </div>

        {/* Download progress */}
        {[
          DOWNLOAD_STATUS.DOWNLOADING,
          DOWNLOAD_STATUS.PAUSED,
          DOWNLOAD_STATUS.ERROR,
        ].includes(fileState?.status) && (
            <div className="flex items-center gap-2 mt-1 px-1">
              <Progress
                className="flex-1 !m-0"
                percent={fileState?.progress}
                showInfo={false}
                strokeColor={
                  fileState?.status === DOWNLOAD_STATUS.ERROR
                    ? "#ff4d4f"
                    : "#52c41a"
                }
                size="small"
              />
              <div className="text-gray-500 text-[10px] whitespace-nowrap">{progressRate}</div>
            </div>
          )}
      </div>

      {isFilePreviewModal && (
        <FilePreviewModal
          isOpen={isFilePreviewModal}
          onClose={() => setIsFilePreviewModal(false)}
          urlPreview={urlPreview}
          resourceId={file?.resourceId}
        />
      )}

      {isOpenMediaModal && (
        <MediaStorageModal
          file={fileChoose}
          onClose={() => setIsOpenMediaModal(false)}
        />
      )}
    </>
  );
};

export default StorageFile;
